import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Image as ImageIcon, Video, FileText, Pencil, Plus, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEvent } from "@/context/AppContext";
import { api, toastErr } from "@/lib/api";
import { NO_AUTOCORRECT } from "@/lib/typing";

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Telegram Business quick replies of the chat's profile (read only, synced from Telegram).
export function useQuickReplies(convId, accountId) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/conversations/${convId}/quick-replies`).then((r) => setItems(r.data.items)).catch(() => setItems([])), [convId]);
  useEffect(() => { load(); }, [load]);
  useEvent("QUICK_REPLIES_UPDATED", (d) => d.account_id === accountId && load());
  return items;
}

export const filterQuick = (items, q) => (items || []).filter((x) => x.shortcut.toLowerCase().startsWith(q.toLowerCase()));

const KIND = { photo: ImageIcon, gif: ImageIcon, video: Video };

export function QuickReplyList({ items, active = -1, onPick, onHover, onEdit }) {
  if (!items) return <div className="p-3 text-xs text-slate-500">Caricamento…</div>;
  if (!items.length) return <div className="p-3 text-xs text-slate-400" data-testid="quick-replies-empty">Nessuna risposta rapida: creale su Telegram scrivendo / in una chat</div>;
  return (
    <div className="max-h-72 overflow-y-auto thin-scroll py-1" data-testid="quick-replies-list">
      {items.map((q, i) => {
        const Icon = KIND[q.kinds?.[0]] || (q.kinds?.length ? FileText : null);
        return (
          <button key={q.id} type="button" onMouseDown={(e) => { e.preventDefault(); onPick(q); }} onMouseEnter={() => onHover?.(i)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${i === active ? "bg-violet-600/25" : "hover:bg-white/5"}`} data-testid={`quick-reply-${q.shortcut}`}>
            <span className="shrink-0 text-sm font-bold text-violet-300">/{q.shortcut}</span>
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
            <span className="min-w-0 flex-1 truncate text-xs text-slate-400">{q.preview || (q.kinds?.length ? "Media" : "")}</span>
            {q.count > 1 && <span className="shrink-0 rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-bold text-slate-400" data-testid={`quick-reply-more-${q.shortcut}`}>+ ALTRE {q.count - 1}</span>}
            {onEdit && <span role="button" tabIndex={-1} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(q); }} title="Modifica su Telegram"
              className="shrink-0 rounded p-1 text-slate-500 hover:bg-white/10 hover:text-white" data-testid={`quick-reply-edit-${q.shortcut}`}><Pencil className="h-3 w-3" /></span>}
          </button>
        );
      })}
    </div>
  );
}

// Create / edit a Telegram quick reply (written on Telegram, then re-read): rename, change texts, add messages. Nothing is ever deleted.
function QuickReplyEditor({ q, accountId, onClose }) {
  const isNew = !q?.id;
  const [name, setName] = useState(q?.shortcut || "");
  const [msgs, setMsgs] = useState((q?.messages || []).map((m) => ({ ...m, orig: m.text })));
  const [added, setAdded] = useState(isNew ? [""] : []);
  const [busy, setBusy] = useState(false);
  const [cid] = useState(newId);
  const save = async () => {
    setBusy(true);
    try {
      const add = added.filter((t) => t.trim());
      if (isNew) await api.post(`/telegram/accounts/${accountId}/quick-replies`, { client_id: cid, shortcut: name, add });
      else await api.patch(`/quick-replies/${q.id}`, { client_id: cid, shortcut: name !== q.shortcut ? name : undefined, add,
        edits: msgs.filter((m) => m.text.trim() && m.text !== m.orig).map((m) => ({ tg_id: m.tg_id, text: m.text })) });
      toast.success(`/${name} salvata su Telegram`);
      onClose();
    } catch (e) { toastErr(e); } finally { setBusy(false); }
  };
  const box = "w-full rounded-md border border-[#232738] bg-[#0D0E14] px-2.5 py-2 text-sm text-white outline-none focus:border-violet-500";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#12141D] border-[#232738]" data-testid="quick-reply-editor">
        <DialogHeader><DialogTitle>{isNew ? "Nuova risposta rapida" : `Modifica /${q.shortcut}`}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Le modifiche vengono salvate sul profilo Telegram, come nell'app Telegram.</p>
        <label className="text-xs font-semibold text-slate-400">Nome (si usa scrivendo /nome)</label>
        <div className="flex items-center gap-1"><span className="text-violet-300 font-bold">/</span>
          <input value={name} onChange={(e) => setName(e.target.value.replace(/[^\w]/g, "").slice(0, 32))} className={box} data-testid="quick-reply-name-input" /></div>
        <div className="max-h-[45vh] space-y-2 overflow-y-auto thin-scroll">
          {msgs.map((m, i) => (
            <div key={m.tg_id}>
              <div className="text-[10px] font-bold uppercase text-slate-500">Messaggio {i + 1}{m.kinds?.length ? ` · ${m.kinds.join(", ")} (didascalia)` : ""}</div>
              <textarea {...NO_AUTOCORRECT} rows={2} value={m.text} onChange={(e) => setMsgs((p) => p.map((x) => (x.tg_id === m.tg_id ? { ...x, text: e.target.value } : x)))} className={box} data-testid={`quick-reply-msg-${i}`} />
            </div>
          ))}
          {added.map((t, i) => (
            <div key={`n${i}`}>
              <div className="text-[10px] font-bold uppercase text-emerald-400">Nuovo messaggio</div>
              <textarea {...NO_AUTOCORRECT} rows={2} value={t} onChange={(e) => setAdded((p) => p.map((x, j) => (j === i ? e.target.value : x)))} className={box} data-testid={`quick-reply-new-msg-${i}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setAdded((p) => [...p, ""])} className="flex items-center gap-1 text-xs font-bold text-violet-300 hover:text-violet-200" data-testid="quick-reply-add-msg-btn"><Plus className="h-3.5 w-3.5" />Aggiungi messaggio</button>
          <button onClick={save} disabled={busy || !name} className="flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-violet-500 disabled:opacity-50" data-testid="quick-reply-save-btn">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Salva su Telegram</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuickReplyButton({ items, onPick, accountId, canEdit }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className="flex items-center gap-1 p-2 rounded-md text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5" title="Risposte rapide di Telegram (/)" data-testid="composer-quick-replies-btn">
          <Zap className="h-4 w-4" />Risposte rapide
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0 bg-[#12141D] border-[#232738]">
          <div className="border-b border-[#232738] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Risposte rapide di Telegram</div>
          <QuickReplyList items={items} onPick={(q) => { setOpen(false); onPick(q); }} onEdit={canEdit ? (q) => { setOpen(false); setEditing(q); } : undefined} />
          {canEdit && <button onClick={() => { setOpen(false); setEditing({}); }} className="flex w-full items-center gap-1 border-t border-[#232738] px-3 py-2 text-xs font-bold text-violet-300 hover:bg-white/5" data-testid="quick-reply-new-btn"><Plus className="h-3.5 w-3.5" />Nuova risposta rapida</button>}
        </PopoverContent>
      </Popover>
      {editing && <QuickReplyEditor q={editing} accountId={accountId} onClose={() => setEditing(null)} />}
    </>
  );
}
